import { useState, useCallback, useEffect } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { StatCard, Select, Table, Pagination, Alert, Badge, type TableColumn } from '@/ui'
import { format } from 'date-fns'
import styles from './CreditTransactions.module.css'

interface CreditTransaction {
  id: string
  type: string
  amount: number
  balanceAfter: number
  referenceType: string | null
  referenceId: string | null
  description: string | null
  createdAt: string
  userName: string | null
  userEmail: string
}

interface CreditTxResponse {
  transactions: CreditTransaction[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: { totalCount: number; totalDeducted: number; totalRefunded: number }
}

const TYPE_LABELS: Record<string, string> = {
  DEDUCT: '차감',
  REFUND: '환불',
  PURCHASE: '구매',
  BONUS: '보너스',
}

const TYPE_BADGE_COLOR: Record<string, 'gray' | 'blue' | 'green' | 'red' | 'yellow'> = {
  DEDUCT: 'red',
  REFUND: 'blue',
  PURCHASE: 'green',
  BONUS: 'yellow',
}

export function CreditTransactions() {
  const { filters, setFilters, resetFilters } = useAdminFilters()
  const [typeFilter, setTypeFilter] = useState('')

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (typeFilter) params.set('type', typeFilter)

  const { data, isLoading, error } = useApiFetch<CreditTxResponse>(
    `/api/admin/credit-transactions?${params.toString()}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const columns: TableColumn<CreditTransaction>[] = [
    { key: 'id', label: 'ID', render: (row: CreditTransaction) => row.id.slice(0, 8), width: '80px' },
    { key: 'userName', label: '유저', render: (row: CreditTransaction) => row.userName ?? row.userEmail },
    {
      key: 'type',
      label: '타입',
      render: (row: CreditTransaction) => (
        <Badge color={TYPE_BADGE_COLOR[row.type] ?? 'gray'}>{TYPE_LABELS[row.type] ?? row.type}</Badge>
      ),
      width: '90px',
    },
    {
      key: 'amount',
      label: '수량',
      sortable: true,
      render: (row: CreditTransaction) => {
        const isNegative = row.type === 'DEDUCT' || row.type === 'REFUND'
        return (
          <span className={isNegative ? styles.negative : styles.positive}>
            {isNegative ? '-' : '+'}{row.amount.toLocaleString()}
          </span>
        )
      },
      align: 'right' as const,
      width: '80px',
    },
    {
      key: 'balanceAfter',
      label: '잔액',
      sortable: true,
      render: (row: CreditTransaction) => `${row.balanceAfter.toLocaleString()} C`,
      align: 'right' as const,
      width: '80px',
    },
    { key: 'referenceType', label: '참조', render: (row: CreditTransaction) => row.referenceType ?? '-', width: '80px' },
    { key: 'description', label: '설명', render: (row: CreditTransaction) => row.description ?? '-' },
    {
      key: 'createdAt',
      label: '일시',
      sortable: true,
      render: (row: CreditTransaction) => format(new Date(row.createdAt), 'yyyy.M.d HH:mm'),
      width: '130px',
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 트랜잭션" value={`${(data?.stats.totalCount ?? 0).toLocaleString()}건`} />
        <StatCard label="총 차감" value={`${(data?.stats.totalDeducted ?? 0).toLocaleString()} C`} />
        <StatCard label="총 환불" value={`${(data?.stats.totalRefunded ?? 0).toLocaleString()} C`} />
      </div>

      <div className={styles.filters}>
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 타입' },
            { value: 'DEDUCT', label: '차감' },
            { value: 'REFUND', label: '환불' },
            { value: 'PURCHASE', label: '구매' },
            { value: 'BONUS', label: '보너스' },
          ]}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table
        columns={columns}
        data={data?.transactions ?? []}
        rowKey={(row: CreditTransaction) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page: number) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
